/**
 * Hinweis auf der Uebersicht (F-29): vergangene Termine der letzten Tage,
 * die noch nicht bestaetigt sind. Bestaetigen per Haekchen ist hier erlaubt,
 * weil es dem Abhaken entspricht (V-02); alles andere passiert im
 * Abendjournal, wohin der Link fuehrt.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useOccurrences } from '@/modules/calendar/hooks/useOccurrences';
import { bestaetigen } from '@/db/repositories/bestaetigungen';
import { unbestaetigt } from '@/domain/calendar/bestaetigung';
import { addDays, datePart, formatDe, now } from '@/lib/datetime';
import type { LocalDate, LocalDateTime } from '@/domain/types';

/** So weit zurueck wird erinnert. */
const TAGE = 7;
const MAX = 4;

export function OffeneBestaetigungen({ heute }: { heute: LocalDate }) {
  const ab = addDays(heute, -TAGE);
  const range = useMemo(() => ({
    from: `${ab}T00:00` as LocalDateTime,
    to: `${addDays(heute, 1)}T00:00` as LocalDateTime,
  }), [ab, heute]);
  const offen = unbestaetigt(useOccurrences(range), ab, now());
  if (offen.length === 0) return null;
  const erster = datePart(String(offen[0]!.start));

  return (
    <section aria-label="Noch nicht bestätigt" className="flex flex-col gap-1 rounded-md border border-line bg-surface-raised/40 px-2.5 py-2">
      <h3 className="text-[12px] font-medium">
        {offen.length === 1 ? '1 Termin' : `${offen.length} Termine`} noch nicht bestätigt
      </h3>
      <ul className="flex flex-col">
        {offen.slice(0, MAX).map((o) => (
          <li key={o.occurrenceId}>
            <label className="flex items-center gap-2 rounded px-1 py-0.5 text-[12px] hover:bg-surface-raised">
              <input type="checkbox" checked={false} onChange={() => void bestaetigen(o, 'stattgefunden')}
                aria-label={`${o.title || 'Ohne Titel'} am ${formatDe(String(o.start), 'dd.MM.')} hat stattgefunden`}
                className="size-3.5 shrink-0 accent-[var(--color-accent)]" />
              <span className="w-[6.5rem] shrink-0 tabular-nums text-ink-faint">{formatDe(String(o.start), 'EEEEEE dd.MM. HH:mm')}</span>
              <span className="min-w-0 flex-1 truncate">{o.title || 'Ohne Titel'}</span>
            </label>
          </li>
        ))}
      </ul>
      <Link to={`/journal?datum=${erster}&typ=abend`} className="self-start px-1 text-[12px] text-accent-text hover:underline">
        {offen.length > MAX ? `Alle ${offen.length} im Abendjournal bestätigen` : 'Ausgefallen oder anders? Im Abendjournal bestätigen'}
      </Link>
    </section>
  );
}
